import React, { useEffect, useState } from "react";
import {
  Flex,
  Grid,
  SimpleGrid,
  Text,
  Spinner,
  Box,
  HStack,
  Input,
  Button,
  useColorModeValue,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";

import PieChart from "components/Charts/PieChart";
import DataByDateBarChart from "components/Charts/DataByDateBarChart";
import Statistics from "../Components/Statistics";
import DataByDistrictHeatmap from "components/Charts/DataByDistrictHeatmap";

import MiniStatistics from "../Dashboard/components/MiniStatistics";
import {
  WalletIcon,
  DocumentIcon,
  WarningIcon,
  CartIcon,
  DeliveryIcon,
  SupportIcon,
  GlobeIcon,
  LockerIcon,
} from "components/Icons/Icons";

import { fetchClaimsSummaryAction } from "../../../state/actions/analyticsActions";
import GradientButton from "components/Buttons/GradientButton";

export default function Reports() {
  const dispatch = useDispatch();

  const loading = useSelector((state) => state.analytics.loading);

  const claimsSummary = useSelector((state) => state.analytics.claimsSummary);
  const claimsSummaryLoading = useSelector(
    (state) => state.analytics.claimsSummaryLoading
  );
  const claimsSummaryError = useSelector(
    (state) => state.analytics.claimsSummaryError
  );

  // date picker state (YYYY-MM-DD)
  const [startDate, setStartDate] = useState("2025-03-01");
  const [endDate, setEndDate] = useState("2040-03-01");

  const muted = useColorModeValue("gray.600", "gray.300");

  useEffect(() => {
    // initial fetch
    dispatch(fetchClaimsSummaryAction(startDate, endDate));
  }, [dispatch]);

  const metrics = claimsSummary?.metrics;

  const onLoadSummary = () => {
    if (!startDate || !endDate) return;
    dispatch(fetchClaimsSummaryAction(startDate, endDate));
  };

  return (
    <Flex flexDirection="column" pt={{ base: "120px", md: "75px" }}>
      <Statistics />

      {/* Claims Summary Header + Date Picker */}
      <Box mt={6} mb={2}>
        <Flex
          justify="space-between"
          align="center"
          gap={3}
          flexWrap="wrap"
          mb={2}
        >
          <Box>
            <Text fontSize="lg" fontWeight="700">
              Claims Analytics Summary
            </Text>
            <Text fontSize="sm" color={muted}>
              Range: {claimsSummary?.range?.start || "-"} →{" "}
              {claimsSummary?.range?.end || "-"}
            </Text>
          </Box>

          <HStack spacing={3} flexWrap="nowrap" overflowX="auto">
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              maxW="180px"
            />
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              maxW="180px"
            />
            <GradientButton
              onClick={onLoadSummary}
              isLoading={claimsSummaryLoading}
              loadingText="Loading"
              width='100px'
              height='38px'
              fontSize='sm'
            >
              Load
            </GradientButton>
          </HStack>
        </Flex>

        {
          claimsSummaryLoading && (
            <Flex align="center" gap={2} mt={2}>
              <Spinner size="sm" />
              <Text fontSize="sm" color={muted}>
                Fetching claims summary...
              </Text>
            </Flex>
          )
        }

        {
          claimsSummaryError && (
            <Text fontSize="sm" color="red.400" mt={2}>
              {claimsSummaryError}
            </Text>
          )
        }
      </Box >

      {/* Claims Summary Cards (MiniStatistics style) */}
      < SimpleGrid columns={{ sm: 1, md: 2, xl: 4 }
      } spacing="24px" mt={3} >
        <MiniStatistics
          title="Protection Revenue"
          amount={`P${metrics?.total_protection_revenue ?? 0}`}
          percentage={0}
          icon={<WalletIcon h={"24px"} w={"24px"} color="white" />}
          isLoading={claimsSummaryLoading}
        />

        <MiniStatistics
          title="Claims Submitted"
          amount={metrics?.total_claims_submitted ?? 0}
          percentage={metrics?.claim_frequency_percent ?? 0}
          icon={<DocumentIcon h={"24px"} w={"24px"} color="white" />}
          isLoading={claimsSummaryLoading}
        />

        <MiniStatistics
          title="Claims Paid"
          amount={metrics?.total_claims_paid ?? 0}
          percentage={metrics?.loss_ratio_percent ?? 0}
          icon={<LockerIcon h={"24px"} w={"24px"} color="white" />}
          isLoading={claimsSummaryLoading}
        />

        <MiniStatistics
          title="Paid Amount"
          amount={`P${metrics?.total_claims_paid_amount ?? 0}`}
          percentage={0}
          icon={<CartIcon h={"24px"} w={"24px"} color="white" />}
          isLoading={claimsSummaryLoading}
        />

        <MiniStatistics
          title="Claim Frequency"
          amount={`${metrics?.claim_frequency_percent ?? 0}%`}
          percentage={0}
          icon={<WarningIcon h={"24px"} w={"24px"} color="white" />}
          isLoading={claimsSummaryLoading}
        />

        <MiniStatistics
          title="Loss Ratio"
          amount={`${metrics?.loss_ratio_percent ?? 0}%`}
          percentage={0}
          icon={<GlobeIcon h={"24px"} w={"24px"} color="white" />}
          isLoading={claimsSummaryLoading}
        />

        <MiniStatistics
          title="Protected Packages"
          amount={metrics?.protected_trips ?? 0}
          percentage={0}
          icon={<DeliveryIcon h={"24px"} w={"24px"} color="white" />}
          isLoading={claimsSummaryLoading}
        />

        <MiniStatistics
          title="Exposure / Trip"
          amount={`P${metrics?.exposure_per_driver_trip ?? 0}`}
          percentage={0}
          icon={<SupportIcon h={"24px"} w={"24px"} color="white" />}
          isLoading={claimsSummaryLoading}
        />
      </SimpleGrid >

      {/* Existing charts */}
      < Grid
        templateColumns={{ sm: "1fr", md: "1fr 1fr", lg: "1fr 1fr" }}
        templateRows={{ sm: "1fr auto", md: "1fr", lg: "1fr" }}
        gap="24px"
        mt={6}
      >
        <PieChart
          title="Status Distribution"
          exportFileName="Status_Distribution"
          loading={loading}
          showExportButton={true}
          selectedTable="packages"
        />

        <DataByDateBarChart
          title="Data Overview"
          showExportButton={true}
          fileName="DataByDateChart"
          selectedTable="packages"
        />
      </Grid >

      <Grid
        templateColumns={{ sm: "1fr", md: "1fr", lg: "1fr" }}
        gap="24px"
        mt={6}
      >
        <DataByDistrictHeatmap />
      </Grid>
    </Flex >
  );
}